import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import AuthService from "../../../api/services/AuthService";
import Footer from "../../../customComponents/Footer";
import "./Referral.css";
import "../ConvertPage/convert.css";

const buildReferralLink = (referralCode) => {
  if (!referralCode) return "";
  return `${window.location.origin}/signup?referral_code=${encodeURIComponent(referralCode)}`;
};

const pickReferralCode = (payload) => {
  if (payload == null) return "";
  if (typeof payload === "string" || typeof payload === "number") return String(payload).trim();
  if (typeof payload !== "object") return "";

  // API: { success, message, data: { refer_code: "APP253848" } }
  const fromData =
    payload?.data?.refer_code ||
    payload?.data?.user_code ||
    payload?.data?.referral_code ||
    payload?.data?.referCode ||
    payload?.data?.code;
  if (fromData) return String(fromData).trim();

  const nested = payload.data ?? payload.result ?? payload;
  if (typeof nested === "string" || typeof nested === "number") return String(nested).trim();
  if (typeof nested !== "object" || nested == null) return "";
  return String(
    nested.refer_code ||
    nested.user_code ||
    nested.referral_code ||
    nested.referCode ||
    nested.code ||
    nested.refferal_code ||
    nested.user_refer_code ||
    ""
  ).trim();
};

const pickReferCount = (payload) => {
  if (payload == null) return 0;
  if (typeof payload === "number") return payload;
  if (typeof payload === "string" && payload.trim() !== "" && !Number.isNaN(Number(payload))) {
    return Number(payload);
  }
  if (typeof payload !== "object") return 0;
  // Backend: TotalCount / total_count at root (GET /v1/user/total_refer_count)
  const rootCount = payload.TotalCount ?? payload.total_count;
  if (rootCount != null && !Number.isNaN(Number(rootCount))) return Number(rootCount) || 0;

  const nested = payload.data ?? payload.result ?? payload;
  if (typeof nested === "number") return nested;
  if (typeof nested === "string" && nested.trim() !== "" && !Number.isNaN(Number(nested))) {
    return Number(nested);
  }
  if (typeof nested !== "object" || nested == null) return 0;
  const n =
    nested.TotalCount ??
    nested.total_refer_count ??
    nested.totalReferCount ??
    nested.total_count ??
    nested.count ??
    nested.total ??
    nested.friends ??
    nested.invites ??
    0;
  return Number(n) || 0;
};

const pickHasHistory = (payload, listLength = 0) => {
  if (payload && typeof payload === "object") {
    if (typeof payload.has_history === "boolean") return payload.has_history;
    const nested = payload.data;
    if (nested && typeof nested === "object" && typeof nested.has_history === "boolean") {
      return nested.has_history;
    }
    const count = pickReferCount(payload);
    if (count > 0) return true;
  }
  return listLength > 0;
};

const pickReferList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== "object") return [];
  const nested = payload.data ?? payload.result ?? payload.list ?? payload.users ?? payload;
  if (Array.isArray(nested)) return nested;
  if (nested && typeof nested === "object") {
    if (Array.isArray(nested.list)) return nested.list;
    if (Array.isArray(nested.users)) return nested.users;
    if (Array.isArray(nested.referrals)) return nested.referrals;
  }
  return [];
};

const formatJoinDate = (value) => {
  if (!value) return "-----";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const formatCommissionDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const COMMISSION_PAGE_SIZE = 20;

const marketLabel = (value) => {
  const market = String(value || "").toLowerCase();
  if (market === "spot") return "Spot";
  if (market === "futures") return "Futures";
  return value ? String(value) : "—";
};

const resolveKycBadge = (item) => {
  const rawStatus = item?.kycStatus ?? item?.kyc_status;
  if (rawStatus != null && String(rawStatus).trim() !== "" && Number.isNaN(Number(rawStatus))) {
    const label = String(rawStatus).trim();
    const s = label.toLowerCase();
    if (s === "verified" || s === "approved") return { label, className: "verified" };
    if (s.includes("under review")) return { label, className: "pending" };
    if (s.includes("re-kyc") || s.includes("rekyc")) return { label, className: "not-verified" };
    if (s === "rejected" || s === "declined") return { label, className: "not-verified" };
    if (s.includes("progress") || s === "pending") return { label, className: "pending" };
    return { label, className: "not-verified" };
  }
  const raw =
    rawStatus ??
    item?.kycVerified ??
    item?.kyc_verified ??
    item?.status;
  const s = String(raw ?? "").toLowerCase();
  if (raw === true || raw === 2 || raw === "2" || s === "verified" || s === "approved") {
    return { label: "Verified", className: "verified" };
  }
  if (raw === 1 || raw === "1") {
    return { label: "In progress", className: "pending" };
  }
  if (raw === 4 || raw === "4") {
    return { label: "Under review", className: "pending" };
  }
  if (raw === 3 || raw === "3" || s === "rejected") {
    return { label: "Rejected", className: "not-verified" };
  }
  if (raw === 5 || raw === "5") {
    return { label: "Re-KYC required", className: "not-verified" };
  }
  if (s === "not submitted" || s === "not verified" || s === "unverified" || s === "not started") {
    return { label: "Not started", className: "not-verified" };
  }
  return { label: "Not started", className: "not-verified" };
};

const maskSignId = (value) => {
  const s = String(value || "").trim();
  if (!s) return "-----";
  if (s.length <= 6) return s;
  return `${s.slice(0, 3)}***${s.slice(-4)}`;
};

// const TICKER_COINS = [
//   { pair: "BTC/USDT", price: "67,894.23", change: "+2.45%", chart: "/images/referral-img/market_graph.svg" },
//   { pair: "ETH/USDT", price: "3,215.67", change: "+1.32%", chart: "/images/referral-img/market_graph.svg" },
//   { pair: "BNB/USDT", price: "598.42", change: "+3.21%", chart: "/images/referral-img/market_graph.svg" },
//   { pair: "SOL/USDT", price: "152.67", change: "+4.22%", chart: "/images/referral-img/market_graph.svg" },
// ];

const REFERRAL_FAQ_ITEMS = [
  {
    question: "What is the AGCX Referral Program?",
    answer: "The AGCX Referral Program is an incentive system that rewards you for bringing new users to the platform. By sharing your personal invite link or referral code, you can earn a percentage of the trading fees paid by your invitees on their trades."
  },
  {
    question: "How does the AGCX Referral System work?",
    answer: "When a new user registers using your referral link or enters your code during sign-up, their account is permanently linked to yours. Every time they trade on AGCX, a share of the commission fee paid by them is automatically credited to your referral earnings."
  },
  {
    question: "How can I invite friends to AGCX?",
    answer: "Simply copy your unique referral link or code from the dashboard above and share it with your friends. You can post it on social media, share it in chats, or send it directly. Ensure they use it during signup to successfully link their account."
  },
  {
    question: "When will I receive referral rewards?",
    answer: "Referral commissions are calculated in real-time or daily once the referred user's trades are executed and settled. You can track your pending and total earnings dynamically in the statistics and history sections."
  },
  {
    question: "How are referral commissions calculated?",
    answer: "Commissions are calculated as a percentage of the trading fees paid by your invitees. You start with a base rate of 15% of their fees. As your network's trading volume grows, you can unlock higher tiers rising to 25% and 35% commission rates."
  }
];

const ReferralPageNew = () => {
  const navigate = useNavigate();
  const sidebarScrollRef = useRef(null);
  const [copiedLink, setCopiedLink] = useState(false);
  const [copiedCode, setCopiedCode] = useState(false);
  const [activeTab, setActiveTab] = useState("referral_history");
  const [selectedTimeframe, setSelectedTimeframe] = useState("Last 30 Days");
  const [timeframeDropdownOpen, setTimeframeDropdownOpen] = useState(false);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  const [referralCode, setReferralCode] = useState("");
  const [referCount, setReferCount] = useState(0);
  const [referList, setReferList] = useState([]);
  const [hasHistory, setHasHistory] = useState(false);
  const [referralLoading, setReferralLoading] = useState(false);
  const [commissionRows, setCommissionRows] = useState([]);
  const [commissionLoading, setCommissionLoading] = useState(false);
  const [commissionError, setCommissionError] = useState("");
  const [commissionPage, setCommissionPage] = useState(1);
  const [commissionTotal, setCommissionTotal] = useState(0);
  const [commissionMarket, setCommissionMarket] = useState("");

  const loadReferralData = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) return;

    setReferralLoading(true);
    try {
      const [codeRes, countRes, listRes] = await Promise.all([
        AuthService.refferlsList(),
        AuthService.totalReferCount(),
        AuthService.getReferList(),
      ]);

      if (codeRes?.success) {
        const code = pickReferralCode(codeRes);
        if (code) setReferralCode(code);
      }

      const list = listRes?.success ? pickReferList(listRes) : [];
      if (listRes?.success) {
        setReferList(list);
      } else {
        setReferList([]);
      }

      let count = 0;
      if (countRes?.success) {
        count = pickReferCount(countRes);
      } else if (listRes?.success) {
        count = list.length;
      }
      // Prefer list length when API returns total_count on list (keeps friends + history in sync)
      if (listRes?.success && typeof listRes.total_count === "number") {
        count = listRes.total_count;
      } else if (listRes?.success && list.length > count) {
        count = list.length;
      }
      setReferCount(count);

      const history =
        pickHasHistory(listRes, list.length) ||
        pickHasHistory(countRes, count) ||
        count > 0 ||
        list.length > 0;
      setHasHistory(history);
    } catch {
      /* keep UI defaults on network failure */
      setReferList([]);
      setReferCount(0);
      setHasHistory(false);
    } finally {
      setReferralLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadReferralData();
  }, [loadReferralData]);

  const loadCommissionReport = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      setCommissionRows([]);
      setCommissionTotal(0);
      setCommissionError("");
      return;
    }
    setCommissionLoading(true);
    setCommissionError("");
    try {
      const res = await AuthService.getReferralCommissionHistory({
        page: commissionPage,
        limit: COMMISSION_PAGE_SIZE,
        market: commissionMarket || undefined,
      });
      if (res?.success) {
        setCommissionRows(Array.isArray(res.data) ? res.data : []);
        const total = Number(res.total_count);
        setCommissionTotal(Number.isFinite(total) ? total : 0);
      } else {
        setCommissionRows([]);
        setCommissionTotal(0);
        setCommissionError(res?.message || "Could not load the commission report.");
      }
    } catch {
      setCommissionRows([]);
      setCommissionTotal(0);
      setCommissionError("Could not load the commission report.");
    } finally {
      setCommissionLoading(false);
    }
  }, [commissionPage, commissionMarket]);

  useEffect(() => {
    if (activeTab !== "commission_report") return;
    void loadCommissionReport();
  }, [activeTab, loadCommissionReport]);

  const commissionPageCount = Math.max(1, Math.ceil(commissionTotal / COMMISSION_PAGE_SIZE));
  const loggedIn = Boolean(typeof window !== "undefined" && localStorage.getItem("token"));

  const checkScroll = () => {
    const el = sidebarScrollRef.current;
    if (el) {
      const hasOverflow = el.scrollWidth > el.clientWidth;
      const isScrollableLeft = el.scrollLeft > 5;
      const isScrollableRight = el.scrollLeft < el.scrollWidth - el.clientWidth - 5;
      setShowLeftArrow(hasOverflow && isScrollableLeft);
      setShowRightArrow(hasOverflow && isScrollableRight);
    }
  };

  useEffect(() => {
    checkScroll();
    window.addEventListener("resize", checkScroll);
    return () => window.removeEventListener("resize", checkScroll);
  }, []);

  useEffect(() => {
    const el = sidebarScrollRef.current;
    if (!el) return;
    el.addEventListener("scroll", checkScroll);
    const timer = setTimeout(checkScroll, 100);
    return () => {
      el.removeEventListener("scroll", checkScroll);
      clearTimeout(timer);
    };
  }, [sidebarScrollRef.current]);

  useEffect(() => {
    checkScroll();
  }, [activeTab]);

  useEffect(() => {
    if (!timeframeDropdownOpen) return;
    const handleOutsideClick = (e) => {
      if (!e.target.closest(".time-select-dropdown-container")) {
        setTimeframeDropdownOpen(false);
      }
    };
    document.addEventListener("click", handleOutsideClick);
    return () => document.removeEventListener("click", handleOutsideClick);
  }, [timeframeDropdownOpen]);

  const [startDate, setStartDate] = useState("2024-05-01");
  const [endDate, setEndDate] = useState("2024-05-31");
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  const toggleFaq = (i) => {
    setOpenFaq((v) => (v === i ? null : i));
  };

  const formatDateDisplay = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric"
    });
  };

  useEffect(() => {
    if (!datePickerOpen) return;
    const handleOutsideClick = (e) => {
      if (!e.target.closest(".date-range-container")) {
        setDatePickerOpen(false);
      }
    };
    document.addEventListener("click", handleOutsideClick);
    return () => document.removeEventListener("click", handleOutsideClick);
  }, [datePickerOpen]);

  const referralLink = useMemo(() => buildReferralLink(referralCode), [referralCode]);
  const friendsInvited = referCount || referList.length;

  const copyToClipboard = (text, type) => {
    if (!localStorage.getItem("token")) {
      navigate("/login");
      return;
    }
    if (!text) return;
    navigator.clipboard.writeText(text);
    if (type === "link") {
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
    } else {
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    }
  };

  return (

    <>

      <Helmet>
        <title>Referral Program | Invite Friends & Earn on AGCX </title>
        <meta
          name="description"
          content="Invite friends to AGCX and earn commission on their trading activity. Simple,
           transparent referral rewards for UAE and global users."
        />
        <meta name="keywords" content="crypto referral program, invite and earn crypto UAE " />
        <link rel="canonical" href="https://arabglobal.ae/referral" />
      </Helmet>

      <div className="referral-page-outer">
        <div className="container">
          <div className="referral-container">

            {/* Left Column - Text Content */}
            <div className="referral-hero-left">
              <div className="referral-badge">AGCX Referral Program</div>
              <h1 className="referral-title">
                Invite. Earn.<br />
                <span className="gold-text">Grow Together.</span>
              </h1>
              <p className="referral-subtitle">
                Invite your friends to AGCX and earn up to <span className="gold-text-accent">35%</span> commission on every trade.
              </p>
              <div className="referral-actions">
                <button className="invite-btn" onClick={() => copyToClipboard(referralLink, "link")}>
                  {copiedLink ? "Link Copied!" : "Invite Friends"} <span>→</span>
                </button>
                {/* <button className="how-it-works-btn">How It Works</button> */}
              </div>
            </div>

            {/* Center Column - 3D Chest Render */}
            <div className="referral-hero-center">
              <img src="/images/referral-img/invite_earn_img.png" alt="Referral Rewards" className="chest-image" />
            </div>

            {/* Right Column - User Stats Card */}
            {/* <div className="referral-hero-right">
              <div className="referral-card">

                <div className="card-top">
                  <span className="card-label">Your Referral Code</span>
                  <div className="code-row">
                    <span className="code-value">{referralCode || (referralLoading ? "…" : "—")}</span>
                  </div>
                </div>

                <div className="stats-grid">
                  <div className="stat-item">
                    <span className="stat-label">Your Commission Rate</span>
                    <span className="stat-val text-gold">15%</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Friends' Kickback Rate</span>
                    <span className="stat-val">0%</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Friends You've Invited</span>
                    <span className="stat-val">{friendsInvited}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Total Rewards Earned</span>
                    <span className="stat-val">0 USDT</span>
                  </div>
                </div>

                <div className="share-link-section">
                  <span className="share-label">Share Your Link</span>
                  <div className="share-input-wrap">
                    <input type="text" readOnly value={referralLink || "Sign in to get your referral link"} className="share-input" />
                    <button
                      className="share-copy-btn"
                      onClick={() => copyToClipboard(referralLink, "link")}
                      title="Copy Link"
                      disabled={!referralLink}
                    >
                      <i className={copiedLink ? "ri-check-line" : "ri-checkbox-multiple-blank-line"}></i>
                    </button>
                  </div>
                </div>

              </div>
            </div> */}

          </div>
        </div>
        {/* 
        <div className="ticker-wrap">
          <div className="ticker-track">
            <div className="ticker-group">
              {[...Array(4)].map((_, groupIdx) => (
                <React.Fragment key={`group-1-${groupIdx}`}>
                  {TICKER_COINS.map((coin, idx) => (
                    <div key={`coin-1-${groupIdx}-${idx}`} className="ticker-item">
                      <span className="ticker-pair">{coin.pair}</span>
                      <span className="ticker-price">{coin.price}</span>
                      <span className="ticker-change">{coin.change}</span>
                      <img src={coin.chart} alt="chart" className="ticker-chart" />
                      <span className="ticker-divider">|</span>
                    </div>
                  ))}
                </React.Fragment>
              ))}
            </div>
            <div className="ticker-group" aria-hidden="true">
              {[...Array(4)].map((_, groupIdx) => (
                <React.Fragment key={`group-2-${groupIdx}`}>
                  {TICKER_COINS.map((coin, idx) => (
                    <div key={`coin-2-${groupIdx}-${idx}`} className="ticker-item">
                      <span className="ticker-pair">{coin.pair}</span>
                      <span className="ticker-price">{coin.price}</span>
                      <span className="ticker-change">{coin.change}</span>
                      <img src={coin.chart} alt="chart" className="ticker-chart" />
                      <span className="ticker-divider">|</span>
                    </div>
                  ))}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div> */}

        {/* Overview Section */}
        <div className="referral-overview-section">
          <div className="container">
            {/* Header Row */}
            <div className="overview-section-header">
              <h2 className="overview-section-title">Overview</h2>
              <div className="time-select-dropdown-container">
                <div
                  className={`time-select-dropdown ${timeframeDropdownOpen ? "open" : ""}`}
                  onClick={() => setTimeframeDropdownOpen(!timeframeDropdownOpen)}
                >
                  <span>{selectedTimeframe}</span>
                  <i className={timeframeDropdownOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}></i>
                </div>
                {timeframeDropdownOpen && (
                  <div className="time-select-menu">
                    {["Last 10 Days", "Last 20 Days", "Last 30 Days", "Last 40 Days"].map((opt) => (
                      <div
                        key={opt}
                        className={`time-select-item ${selectedTimeframe === opt ? "selected" : ""}`}
                        onClick={() => {
                          setSelectedTimeframe(opt);
                          setTimeframeDropdownOpen(false);
                        }}
                      >
                        <span>{opt}</span>
                        {selectedTimeframe === opt && (
                          <span className="check-icon-circle">
                            <i className="ri-check-line"></i>
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Stats Row */}
            <div className="overview-stats-grid referraltbl">

              <div className="d-flex gap-3">

                <div className="overview-stat-card">
                  {/* <div className="stat-card-icon-wrap icon-green">
                  <img src="/images/referral-img/overview_icon.svg" alt="card icon" />
                </div> */}
                  <div className="stat-card-details">
                    <span className="stat-card-label">Your Referral Code</span>
                    <span className="stat-card-val">{referralCode || (referralLoading ? "…" : "—")}</span>
                    {/* <span className="stat-card-sub">≈ $0.00</span> */}
                  </div>
                </div>

                <div className="overview-stat-card">
                  {/* <div className="stat-card-icon-wrap icon-orange">
                  <img src="/images/referral-img/overview_icon2.svg" alt="card icon" />
                </div> */}
                  <div className="stat-card-details">
                    <span className="stat-card-label">Friends You've Invited</span>
                    <span className="stat-card-val">{friendsInvited}</span>
                    {/* <span className="stat-card-sub">≈ $0.00</span> */}
                  </div>
                </div>

              </div>

              <div className="overview-stat-card">

                <div className="share-link-section">
                  <span className="share-label">Share Your Link</span>
                  <div className="share-input-wrap">
                    <input type="text" readOnly value={referralLink || "Sign in to get your referral link"} className="share-input" />
                    <button
                      className="share-copy-btn"
                      onClick={() => copyToClipboard(referralLink, "link")}
                      title="Copy Link"
                      disabled={!referralLink}
                    >
                      <i className={copiedLink ? "ri-check-line" : "ri-checkbox-multiple-blank-line"}></i>
                    </button>
                  </div>
                </div>

                {/* <div className="stat-card-icon-wrap icon-blue">
                  <img src="/images/referral-img/overview_icon3.svg" alt="card icon" />
                </div>
                <div className="stat-card-details">
                  <span className="stat-card-label">Total Friends</span>
                  <span className="stat-card-val">{friendsInvited}</span>
                  <span className="stat-card-sub">From your referrals</span>
                </div> */}
              </div>

              {/* <div className="overview-stat-card">
                <div className="stat-card-icon-wrap icon-purple">
                  <img src="/images/referral-img/overview_icon4.svg" alt="card icon" />
                </div>
                <div className="stat-card-details">
                  <span className="stat-card-label">Total Trades</span>
                  <span className="stat-card-val">0</span>
                  <span className="stat-card-sub">+0 this month</span>
                </div>
              </div> */}

            </div>

            {/* Main Layout (Sidebar + Tab Content) */}
            <div className="overview-content-layout">

              {/* Sidebar */}
              <div className="overview-tab-sidebar-wrapper">
                {showLeftArrow && (
                  <button
                    className="sidebar-scroll-arrow left"
                    onClick={() => {
                      if (sidebarScrollRef.current) {
                        sidebarScrollRef.current.scrollBy({ left: -150, behavior: "smooth" });
                      }
                    }}
                    aria-label="Scroll Left"
                  >
                    <i className="ri-arrow-left-s-line"></i>
                  </button>
                )}
                <div className="overview-tab-sidebar" ref={sidebarScrollRef}>
                  {/* <button
                    className={`tab-sidebar-btn ${activeTab === "overview" ? "active" : ""}`}
                    onClick={() => setActiveTab("overview")}
                  >
                    <i className="ri-pie-chart-2-line" aria-hidden="true" />
                    <span>Overview</span>
                  </button> */}
                  <button
                    type="button"
                    className={`tab-sidebar-btn ${activeTab === "referral_history" ? "active" : ""}`}
                    onClick={() => setActiveTab("referral_history")}
                  >
                    <i className="ri-user-shared-line" aria-hidden="true" />
                    <span>Referral History</span>
                  </button>
                  <button
                    type="button"
                    className={`tab-sidebar-btn ${activeTab === "commission_report" ? "active" : ""}`}
                    onClick={() => setActiveTab("commission_report")}
                  >
                    <i className="ri-hand-coin-line" aria-hidden="true" />
                    <span>Commission Report</span>
                  </button>
                </div>
                {showRightArrow && (
                  <button
                    className="sidebar-scroll-arrow right"
                    onClick={() => {
                      if (sidebarScrollRef.current) {
                        sidebarScrollRef.current.scrollBy({ left: 150, behavior: "smooth" });
                      }
                    }}
                    aria-label="Scroll Right"
                  >
                    <i className="ri-arrow-right-s-line"></i>
                  </button>
                )}
              </div>

              {/* Tab Content Panel */}
              <div className="overview-tab-content-panel">
                {activeTab === "overview" && (
                  <div className="commission-performance-wrap">

                    {/* <div className="panel-inner-header">
                      <h3 className="panel-inner-title">Commission Performance</h3>
                      <div className="date-range-container">
                        <div
                          className="date-range-badge"
                          onClick={() => setDatePickerOpen(!datePickerOpen)}
                        >
                          <i className="ri-calendar-line"></i>
                          <span>{formatDateDisplay(startDate)} - {formatDateDisplay(endDate)}</span>
                        </div>
                        {datePickerOpen && (
                          <div className="date-picker-popover">
                            <div className="date-picker-field">
                              <label>Start Date</label>
                              <input
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                className="date-picker-input"
                              />
                            </div>
                            <div className="date-picker-field">
                              <label>End Date</label>
                              <input
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                className="date-picker-input"
                              />
                            </div>
                            <button
                              className="date-picker-confirm-btn"
                              onClick={() => setDatePickerOpen(false)}
                            >
                              Apply
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="panel-grid-body">

                     
                      <div className="panel-left-col">
                        <div className="base-commission-gauge-card">
                          <span className="gauge-card-label">Your Base Commission Rate</span>
                          <div className="gauge-box">
                            <svg width="220" height="120" viewBox="0 0 100 50" className="gauge-svg">
                              <path
                                d="M 10 45 A 40 40 0 0 1 90 45"
                                fill="none"
                                stroke="#222222"
                                strokeWidth="9"
                                strokeLinecap="round"
                              />
                              <path
                                d="M 10 45 A 40 40 0 0 1 90 45"
                                fill="none"
                                stroke="#2BC287"
                                strokeWidth="9"
                                strokeLinecap="round"
                                strokeDasharray="125.6"
                                strokeDashoffset={125.6 - (0.15 * 125.6)}
                              />
                            </svg>
                            <div className="gauge-inner-val">15%</div>
                          </div>
                        </div>
                      </div>

                      
                      <div className="panel-right-col">

                        <div className="upgrade-goal-card">
                          <div className="upgrade-goal-info">
                            <span className="upgrade-title">Reach <span className="text-highlight">$1,000 USDT</span> in trading volume</span>
                            <span className="upgrade-desc">your commission rate will rise to</span>
                          </div>
                          <span className="upgrade-rate-val">25% <img src="/images/referral-img/up_trade.svg" alt="up icon" /></span>
                        </div>

                        <div className="upgrade-goal-card">
                          <div className="upgrade-goal-info">
                            <span className="upgrade-title">Reach <span className="text-highlight">$10,000 USDT</span> in trading volume</span>
                            <span className="upgrade-desc">your commission rate will rise to</span>
                          </div>
                          <span className="upgrade-rate-val">35% <img src="/images/referral-img/up_trade.svg" alt="up icon" /></span>
                        </div>

                      </div>

                    </div> */}

                    {/* Bottom Stats Row */}
                    {/* <div className="panel-bottom-stats-row">

                      <div className="panel-bottom-card">
                        <div className="bottom-card-details">
                          <div className="bottom-card-icon-wrap">
                            <img src="/images/referral-img/invites_icon.svg" alt="card icon" />
                          </div>
                          <div className="bottom-card-texts">
                            <span className="bottom-card-label">Valid Invites</span>
                            <span className="bottom-card-val">{friendsInvited}</span>
                          </div>
                        </div>
                        <div className="bottom-card-sparkline">
                          <img src="/images/referral-img/trade_vector.svg" alt="card icon" />
                        </div>
                      </div>

                      <div className="panel-bottom-card">
                        <div className="bottom-card-details">
                          <div className="bottom-card-icon-wrap">
                            <img src="/images/referral-img/usdt_icon2.svg" alt="card icon" />
                          </div>
                          <div className="bottom-card-texts">
                            <span className="bottom-card-label">Trading Volume</span>
                            <span className="bottom-card-val">0 USDT</span>
                          </div>
                        </div>
                        <div className="bottom-card-sparkline">
                          <img src="/images/referral-img/trade_vector2.svg" alt="card icon" />
                        </div>
                      </div>

                    </div> */}

                  </div>
                )}

                {activeTab === "referral_history" && (
                  <div className={`history-tab-panel-wrap${!hasHistory ? " empty" : ""}`}>
                    <h3 className="panel-inner-title table-title">Referral History</h3>
                    {!hasHistory || referList.length === 0 ? (
                      <div className="panel-empty-state-wrap">
                        <img src="/images/no-data.svg" alt="No data" className="empty-state-img" />
                        <span className="empty-state-text">
                          {referralLoading ? "Loading referrals…" : "No referral history found"}
                        </span>
                      </div>
                    ) : (
                      <div className="panel-table-container">
                        <table className="overview-data-table">
                          <thead>
                            <tr>
                              <th>S.No</th>
                              <th>Name</th>
                              <th>Sign ID</th>
                              <th>KYC Status</th>
                              <th>Join Date</th>
                            </tr>
                          </thead>
                          <tbody>
                            {referList.map((item, index) => {
                              const kyc = resolveKycBadge(item);
                              // API: full_name_masked, masked_id, kyc_status, signup_date
                              const name =
                                item?.full_name_masked ||
                                item?.name ||
                                item?.fullName ||
                                item?.full_name ||
                                item?.email ||
                                "-----";
                              const signId =
                                item?.masked_id ||
                                item?.signId ||
                                item?.sign_id ||
                                item?.userId ||
                                item?.user_id ||
                                item?._id ||
                                item?.email ||
                                "";
                              const joinDate =
                                item?.signup_date ||
                                item?.date ||
                                item?.createdAt ||
                                item?.created_at ||
                                item?.joinDate ||
                                item?.join_date;
                              return (
                                <tr key={item?.masked_id || item?._id || item?.email || index}>
                                  <td data-label="S.No">{index + 1}</td>
                                  <td data-label="Name">{name}</td>
                                  <td data-label="Sign ID">
                                    {item?.masked_id ? signId : maskSignId(signId)}
                                  </td>
                                  <td data-label="KYC Status">
                                    <span className={`kyc-badge ${kyc.className}`}>{kyc.label}</span>
                                  </td>
                                  <td data-label="Join Date">{formatJoinDate(joinDate)}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}

                {activeTab === "commission_report" && (
                  <div className={`history-tab-panel-wrap${commissionRows.length === 0 ? " empty" : ""}`}>
                    <div className="commission-report-head">
                      <h3 className="panel-inner-title table-title">Commission Report</h3>
                      <div className="commission-market-filters" role="tablist" aria-label="Market">
                        {[
                          { value: "", label: "All" },
                          { value: "spot", label: "Spot" },
                          { value: "futures", label: "Futures" },
                        ].map((option) => (
                          <button
                            key={option.label}
                            type="button"
                            className={`commission-market-btn${commissionMarket === option.value ? " active" : ""}`}
                            aria-pressed={commissionMarket === option.value}
                            onClick={() => {
                              setCommissionMarket(option.value);
                              setCommissionPage(1);
                            }}
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    {!loggedIn ? (
                      <div className="panel-empty-state-wrap">
                        <img src="/images/no-data.svg" alt="" className="empty-state-img" />
                        <span className="empty-state-text">Log in to view your commission report</span>
                      </div>
                    ) : commissionLoading ? (
                      <div className="panel-empty-state-wrap">
                        <span className="empty-state-text">Loading commissions…</span>
                      </div>
                    ) : commissionError ? (
                      <div className="panel-empty-state-wrap">
                        <img src="/images/no-data.svg" alt="" className="empty-state-img" />
                        <span className="empty-state-text">{commissionError}</span>
                      </div>
                    ) : commissionRows.length === 0 ? (
                      <div className="panel-empty-state-wrap">
                        <img src="/images/no-data.svg" alt="" className="empty-state-img" />
                        <span className="empty-state-text">No commission earned yet</span>
                      </div>
                    ) : (
                      <>
                        <div className="panel-table-container">
                          <table className="overview-data-table">
                            <thead>
                              <tr>
                                <th>Pair</th>
                                <th>Market</th>
                                <th>From</th>
                                <th>Commission</th>
                                <th>Date</th>
                              </tr>
                            </thead>
                            <tbody>
                              {commissionRows.map((row, index) => (
                                <tr key={`${row.pair || "pair"}-${row.created_at || index}-${index}`}>
                                  <td data-label="Pair">{row.pair || "—"}</td>
                                  <td data-label="Market">{marketLabel(row.market)}</td>
                                  <td data-label="From">{row.from_uuid || "—"}</td>
                                  <td data-label="Commission">
                                    {row.commission_amount || "0"} {row.currency || ""}
                                  </td>
                                  <td data-label="Date">{formatCommissionDate(row.created_at)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        {commissionTotal > COMMISSION_PAGE_SIZE ? (
                          <div className="commission-pager">
                            <button
                              type="button"
                              className="commission-pager-btn"
                              disabled={commissionPage <= 1 || commissionLoading}
                              onClick={() => setCommissionPage((page) => Math.max(1, page - 1))}
                            >
                              Previous
                            </button>
                            <span>
                              {commissionPage} / {commissionPageCount}
                            </span>
                            <button
                              type="button"
                              className="commission-pager-btn"
                              disabled={commissionPage >= commissionPageCount || commissionLoading}
                              onClick={() => setCommissionPage((page) => page + 1)}
                            >
                              Next
                            </button>
                          </div>
                        ) : null}
                      </>
                    )}
                  </div>
                )}
              </div>

            </div>

            {/* How to Invite Section */}
            <div className="how-to-invite-section">
              <div className="how-to-invite-header">
                <div className="how-to-invite-title-wrap">
                  <h2 className="how-to-invite-title">
                    How to <span className="gold-text">Invite?</span>
                  </h2>
                  <p className="how-to-invite-subtitle">Get started with referrals and grow your rewards.</p>
                </div>

              </div>

              <div className="invite-steps-container">
                {/* Step 1 */}
                <div className="invite-step-wrapper">
                  <div className="invite-step-number">01</div>
                  <div className="invite-step-card">
                    <div className="invite-step-icon-wrap">
                      <img src="/images/referral-img/share_icon.svg" alt="Share Icon" />
                    </div>
                    <h3 className="invite-step-title">Share Your Referral<br />Code or Link</h3>
                    <div className="invite-step-divider"></div>
                    <p className="invite-step-desc">Refer friends to AGCX &<br />get rewarded.</p>
                  </div>
                </div>

                <div className="card-connector"><div className="connector-dot"></div></div>

                {/* Step 2 */}
                <div className="invite-step-wrapper">
                  <div className="invite-step-number">02</div>
                  <div className="invite-step-card">
                    <div className="invite-step-icon-wrap">
                      <img src="/images/referral-img/link_friends_icon.svg" alt="Link Friends Icon" />
                    </div>
                    <h3 className="invite-step-title">Link Up with<br />Friends</h3>
                    <div className="invite-step-divider"></div>
                    <p className="invite-step-desc">Friends connect upon<br />registration.</p>
                  </div>
                </div>

                <div className="card-connector"><div className="connector-dot"></div></div>

                {/* Step 3 */}
                <div className="invite-step-wrapper">
                  <div className="invite-step-number">03</div>
                  <div className="invite-step-card">
                    <div className="invite-step-icon-wrap">
                      <img src="/images/referral-img/earn_link_icon.svg" alt="Earn Commissions Icon" />
                    </div>
                    <h3 className="invite-step-title">Earn Commissions<br />and More</h3>
                    <div className="invite-step-divider"></div>
                    <p className="invite-step-desc">Earn rewards when your<br />friends start trading.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* FAQ Section */}
            <section className="convert-page__faqSection referralfaq" aria-labelledby="convert-page-faq-title">
              <h2 id="convert-page-faq-title" className="convert-page__faqTitle">
                FAQ
              </h2>
              <ul className="convert-page__faqList">
                {REFERRAL_FAQ_ITEMS.map((item, i) => (
                  <li key={i} className="convert-page__faqItem">
                    <button
                      type="button"
                      className="convert-page__faqBtn"
                      onClick={() => toggleFaq(i)}
                      aria-expanded={openFaq === i}
                    >
                      <span>{item.question}</span>
                      <i className={`ri-arrow-down-s-line convert-page__faqChev${openFaq === i ? " convert-page__faqChev--open" : ""}`} aria-hidden />
                    </button>
                    {openFaq === i ? (
                      <div className="convert-page__faqPanel" role="region">
                        <p style={{ margin: 0, color: '#84888C', lineHeight: '1.6' }}>{item.answer}</p>
                      </div>
                    ) : null}
                  </li>
                ))}
              </ul>
            </section>

          </div>

        </div>

      </div>


      <Footer />
    </>

  );
};

export default ReferralPageNew;
